import orderStatusStrings from '@/constants/orderStatusStrings';

/**
 * Columns a save must not change unless the user changed them in the modal. A stale clone
 * otherwise writes `complete -> claimed` (and clears the completion fields) over a Mark
 * Delivered that happened while the modal was open.
 */
export const PROTECTED_FIELDS = ['order_status', 'completion_time', 'completion_source'];

/**
 * Whether Done should run the stale-save guard. Unsaved orders (/import) and job flows
 * (`isNewOrder`) save exactly as before.
 *
 * @param {{ isNewOrder: boolean, order?: { order_id?: string } }} args
 * @returns {boolean}
 */
export const shouldGuardSave = ({ isNewOrder, order }) => !isNewOrder && !!order?.order_id;

/**
 * True when the stored version differs from the one the modal opened with. Raw strings are
 * compared so microsecond precision survives. A missing side means "cannot tell", not stale.
 *
 * @param {string|null|undefined} baseVersion `last_modified_at` the modal opened with
 * @param {{ last_modified_at?: string|null }|null|undefined} stored row from the guard read
 * @returns {boolean}
 */
export const isStale = (baseVersion, stored) => {
    if (!baseVersion || !stored?.last_modified_at) return false;
    return baseVersion !== stored.last_modified_at;
};

/**
 * Replaces each protected column the user did not change with the stored value. Values are
 * replaced, never added or removed, so the payload keeps every key callers and UPSERT_ORDERS see today.
 *
 * "Changed" means the clone differs from `base` (the order prop, or the reloaded order).
 *
 * @param {{ outgoing: object, base: object, stored?: object|null }} args
 * @returns {object} a new outgoing order
 */
export const pinProtectedFields = ({ outgoing, base, stored }) => {
    if (!stored) return outgoing;
    const pinned = { ...outgoing };
    PROTECTED_FIELDS.forEach((field) => {
        // Only keys already in the payload: never add or remove one.
        if (!(field in outgoing) || !(field in stored)) return;
        const userChanged = (outgoing[field] ?? null) !== (base?.[field] ?? null);
        if (!userChanged) pinned[field] = stored[field];
    });
    return pinned;
};

/**
 * Human label for an order status, shared by the conflict dialog and the live-change dialog.
 *
 * @param {string|null|undefined} status
 * @returns {string}
 */
export const statusLabel = (status) => orderStatusStrings[status] || status || 'Unknown';

/**
 * Local-time "updated at" text for a stored `last_modified_at`, or null when there is none.
 *
 * @param {string|null|undefined} lastModifiedAt
 * @returns {string|null}
 */
export const formatChangedAt = (lastModifiedAt) => (lastModifiedAt ? new Date(lastModifiedAt).toLocaleString() : null);

/**
 * The live feed row when it shows a version other than the one the modal is editing, else null.
 * A missing base, a null feed row, or equal versions all mean "no change to report".
 *
 * @param {{ baseVersion?: string|null, live?: { last_modified_at?: string|null }|null }} args
 * @returns {object|null}
 */
export const liveConflict = ({ baseVersion, live }) => (isStale(baseVersion, live) ? live : null);
